import { alpha, createTheme } from "@mui/material/styles";

const primaryMain = "#2563eb";
const secondaryMain = "#14b8a6";
const backgroundDefault = "#07111f";
const backgroundPaper = "#0f1b2d";

const theme = createTheme({
	palette: {
		mode: "dark",
		primary: { main: primaryMain },
		secondary: { main: secondaryMain },
		background: {
			default: backgroundDefault,
			paper: backgroundPaper,
		},
		text: {
			primary: "#f8fafc",
			secondary: "#94a3b8",
		},
		divider: "rgba(148, 163, 184, 0.18)",
	},
	shape: {
		borderRadius: 12,
	},
	typography: {
		fontFamily: ['"Inter"', '"Segoe UI"', "sans-serif"].join(","),
		h1: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 800,
			fontSize: "clamp(2.9rem, 6vw, 5.2rem)",
			lineHeight: 1,
			letterSpacing: "-0.045em",
		},
		h2: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 700,
			fontSize: "clamp(2.25rem, 5vw, 4rem)",
			lineHeight: 1,
			letterSpacing: "-0.04em",
		},
		h3: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 700,
			fontSize: "clamp(1.6rem, 2.5vw, 2.35rem)",
			lineHeight: 1.05,
			letterSpacing: "-0.03em",
		},
		h4: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 600,
			letterSpacing: "-0.02em",
		},
		h5: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 600,
		},
		h6: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 600,
		},
		body1: {
			fontSize: "1.05rem",
			lineHeight: 1.8,
			color: "#cbd5e1",
		},
		body2: {
			fontSize: "0.98rem",
			lineHeight: 1.7,
			color: "#94a3b8",
		},
		button: {
			fontFamily: '"Outfit", sans-serif',
			fontWeight: 600,
			fontSize: "1rem",
			letterSpacing: "0.01em",
			textTransform: "none",
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				":root": {
					colorScheme: "dark",
				},
				"html, body, #root": {
					minHeight: "100%",
				},
				body: {
					backgroundColor: backgroundDefault,
					backgroundImage: `
						radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%),
						radial-gradient(circle at 85% 12%, rgba(20, 184, 166, 0.14), transparent 28%),
						linear-gradient(180deg, #07111f 0%, #091423 45%, #0b1729 100%)
					`,
					backgroundAttachment: "fixed",
				},
				"a": {
					color: "inherit",
				},
				"::selection": {
					backgroundColor: alpha(secondaryMain, 0.32),
					color: "#f8fafc",
				},
			},
		},
		MuiContainer: {
			defaultProps: {
				maxWidth: "xl",
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: "999px",
					padding: "0.9rem 1.5rem",
					boxShadow: "none",
					transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow: "0 14px 28px rgba(15, 23, 42, 0.22)",
					},
				},
				containedPrimary: {
					background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
				},
				outlinedSecondary: {
					borderColor: alpha(secondaryMain, 0.45),
					color: "#ccfbf1",
					"&:hover": {
						backgroundColor: alpha(secondaryMain, 0.1),
						borderColor: alpha(secondaryMain, 0.9),
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: "none",
					backgroundColor: alpha(backgroundPaper, 0.72),
					backdropFilter: "blur(16px)",
					border: `1px solid ${alpha("#e2e8f0", 0.08)}`,
					boxShadow: "0 24px 60px rgba(2, 6, 23, 0.28)",
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						backgroundColor: alpha("#0f172a", 0.65),
						"& fieldset": {
							borderColor: alpha("#94a3b8", 0.18),
						},
						"&:hover fieldset": {
							borderColor: alpha("#cbd5e1", 0.3),
						},
						"&.Mui-focused fieldset": {
							borderColor: alpha(primaryMain, 0.8),
						},
					},
				},
			},
		},
	},
});

export default theme;
